/**
 * 前端 JS 错误捕获与上报
 * 上报接口通过环境变量 VUE_APP_ERROR_REPORT_URL 配置，未配置时仅测试环境打印日志
 */
import {reportLog} from "@/utils/api";

const MAX_DEDUPE_CACHE = 100

const ERROR_TYPE = {
  JS_ERROR: 'js_error',
  PROMISE_REJECTION: 'promise_rejection',
  RESOURCE_ERROR: 'resource_error',
  VUE_ERROR: 'vue_error',
  MANUAL: 'manual',
}

let initialized = false
const reportedKeys = new Set()

function getErrorKey(type, message, stack) {
  return `${type}:${message}:${(stack || '').slice(0, 200)}`
}

function shouldReport(type, message, stack) {
  const key = getErrorKey(type, message, stack)
  if (reportedKeys.has(key)) {
    return false
  }
  reportedKeys.add(key)
  if (reportedKeys.size > MAX_DEDUPE_CACHE) {
    const firstKey = reportedKeys.values().next().value
    reportedKeys.delete(firstKey)
  }
  return true
}

function getBaseContext() {
  return {
    url: window.location.href,
    userAgent: navigator.userAgent,
    timestamp: Date.now(),
    channel: process.env.VUE_APP_CHANNEL || '',
    bizLine: process.env.VUE_APP_BIZ_LINE || '',
    nationCode: process.env.VUE_APP_NATION_CODE || '',
  }
}

function normalizeError(error) {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack || '',
      name: error.name,
    }
  }
  if (typeof error === 'string') {
    return { message: error, stack: '', name: 'Error' }
  }
  try {
    return {
      message: JSON.stringify(error),
      stack: '',
      name: 'UnknownError',
    }
  } catch (e) {
    return { message: String(error), stack: '', name: 'UnknownError' }
  }
}

function buildPayload(type, errorInfo, extra = {}) {
  const { message, stack, name, filename, lineno, colno, componentInfo } = errorInfo
  return {
    type,
    message,
    stack,
    name,
    filename,
    lineno,
    colno,
    componentInfo,
    ...getBaseContext(),
    extra,
  }
}

function sendReport(payload) {

  const body = JSON.stringify(payload)

  try {
    reportLog(body);
  } catch (e) {
    // 上报失败不影响业务
  }
}

function dispatchReport(type, errorInfo, extra) {
  const { message, stack } = errorInfo
  if (!message || !shouldReport(type, message, stack)) {
    return
  }
  sendReport(buildPayload(type, errorInfo, extra))
}

function onGlobalError(event) {
  console.error("error event", event);
  const target = event.target

  // 资源加载错误（script / link / img 等）
  if (target && target !== window && (target.src || target.href)) {
    const tagName = target.tagName || 'UNKNOWN'
    const resourceUrl = target.src || target.href || ''
    dispatchReport(ERROR_TYPE.RESOURCE_ERROR, {
      message: `load resource failed: <${tagName.toLowerCase()}> ${resourceUrl}`,
      stack: '',
      name: 'ResourceError',
      filename: resourceUrl,
    })
    return
  }

  // JS 运行时错误
  const error = event.error
  const normalized = error ? normalizeError(error) : { message: event.message, stack: '', name: 'Error' }
  dispatchReport(ERROR_TYPE.JS_ERROR, {
    ...normalized,
    filename: event.filename || '',
    lineno: event.lineno || 0,
    colno: event.colno || 0,
  })
}

function onUnhandledRejection(event) {
  const normalized = normalizeError(event.reason)
  dispatchReport(ERROR_TYPE.PROMISE_REJECTION, normalized)
}

/**
 * 手动上报错误
 * @param {Error|string|*} error
 * @param {object} [extra]
 */
export function reportError(error, extra = {}) {
  const normalized = normalizeError(error)
  dispatchReport(ERROR_TYPE.MANUAL, normalized, extra)
}

/**
 * 初始化全局错误捕获
 * @param {object} [options]
 * @param {import('vue').default} [options.Vue] 传入 Vue 以捕获组件内错误
 */
export function initErrorReport({ Vue } = {}) {
  try {
    doInitErrorReport({Vue});
  } catch (error) {
    console.error(error)
  }
}

export function doInitErrorReport({ Vue } = {}) {
  if (initialized) {
    return
  }
  initialized = true

  window.addEventListener('error', onGlobalError, true)
  window.addEventListener('unhandledrejection', onUnhandledRejection)

  if (Vue) {
    const originalHandler = Vue.config.errorHandler
    Vue.config.errorHandler = (err, vm, info) => {
      const normalized = normalizeError(err)
      dispatchReport(ERROR_TYPE.VUE_ERROR, {
        ...normalized,
        componentInfo: info,
      }, {
        componentName: vm?.$options?.name || vm?.$options?._componentTag || '',
      })
      if (typeof originalHandler === 'function') {
        originalHandler(err, vm, info)
      }
    }
  }
}
